import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { User } from 'firebase/auth';
import { getStandardizedMovieId, migrateMovieId } from '../utils/movieUtils';
import { useDebouncedLocalStorage } from '../hooks/useDebouncedLocalStorage';
import { UserMovieData } from '@watchlist/shared';
import { authService, movieService, isFirebaseInitialized } from '@watchlist/shared';
import { watchlistService } from '../services/watchlistService';
import { dataService } from '../services/dataService';
import { isCapacitorNative, getFirestoreQueryTimeout, getWatchlistQueryTimeout } from '../utils/platformUtils';
import { getCachedUserProfile } from '../utils/profileCache';
import { moviePersonalityService } from '../services/moviePersonalityService';
import { deleteStoryForReview } from '../services/storiesService';

interface UserMovieDataContextType {
  // Movie data cache
  movieDataCache: Map<string, UserMovieData>;

  // Loading states
  isLoading: boolean;

  // Cache version for reactive updates
  cacheVersion: number;

  // Methods to get movie data
  getUserMovieData: (movieId: string) => UserMovieData | null;

  // Methods to update movie data
  updateMovieData: (movieId: string, data: Partial<UserMovieData>) => Promise<void>;
  addToWatchlist: (movieId: string, movieTitle: string, moviePoster?: string, movieYear?: string, shareToFeed?: boolean) => Promise<void>;
  removeFromWatchlist: (movieId: string) => Promise<void>;
  markAsWatched: (
    movieId: string,
    movieTitle: string,
    rating?: number,
    review?: string,
    isPrivate?: boolean,
    moviePoster?: string,
    movieYear?: string,
    visibility?: 'anyone' | 'only-me',
    watchedDateOverride?: Date | string | null,
    watchedDatePrecision?: 'day' | 'month' | 'year',
    shareToFeed?: boolean
  ) => Promise<void>;
  unmarkAsWatched: (movieId: string) => Promise<void>;
  deleteRatingAndReview: (movieId: string) => Promise<void>;
  updateMovieNotes: (movieId: string, note: string) => Promise<void>;

  // Methods to refresh data
  refreshMovieData: (movieId: string) => Promise<void>;
  refreshAllMovieData: () => Promise<void>;

  // Bulk operations
  clearAllUserData: () => Promise<void>;

  // Get lists of movie IDs by status
  getWatchlistMovieIds: () => string[];
  getWatchedMovieIds: () => string[];
  getRatedMovieIds: () => string[];
  getReviewedMovieIds: () => string[];
}

const UserMovieDataContext = createContext<UserMovieDataContextType | null>(null);

export const useUserMovieData = () => {
  const context = useContext(UserMovieDataContext);
  if (!context) {
    throw new Error('useUserMovieData must be used within a UserMovieDataProvider');
  }
  return context;
};

interface UserMovieDataProviderProps {
  children: React.ReactNode;
  user: User | null;
  isFirebaseReady?: boolean;
}

export const UserMovieDataProvider: React.FC<UserMovieDataProviderProps> = ({ children, user, isFirebaseReady = false }) => {
  const { setItem: setDebouncedItem, setItemImmediate } = useDebouncedLocalStorage(300);
  const [movieDataCache, setMovieDataCache] = useState<Map<string, UserMovieData>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [cacheVersion, setCacheVersion] = useState(0);
  const dataLoadedRef = useRef(false);
  const userRef = useRef<User | null>(user);
  const loadingRef = useRef(false); // Prevent concurrent loads

  // Add refs for timeout management to batch cache version updates
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const removeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const invalidateMoviePersonalityCache = useCallback(() => {
    const uid = userRef.current?.uid;
    if (uid) {
      moviePersonalityService.invalidateCache(uid);
    }
  }, []);

  // Add debug logging for user changes
  useEffect(() => {
    console.log('UserMovieDataContext: User changed:', user ? `${user.displayName} (${user.uid})` : 'null');
    const previousUser = userRef.current;
    userRef.current = user;

    // If user changed (not just first load), we need to reset and reload
    if (previousUser && user && previousUser.uid !== user.uid) {
      console.log('UserMovieDataContext: User changed from', previousUser.uid, 'to', user.uid, '- resetting cache');
      setMovieDataCache(new Map());
      dataLoadedRef.current = false;
      setIsLoading(true);
    } else if (!user && previousUser) {
      console.log('UserMovieDataContext: User logged out - clearing cache');
      setMovieDataCache(new Map());
      dataLoadedRef.current = false;
      setIsLoading(false);
    }
  }, [user?.uid]);

  // Add debugging tools for UserMovieDataContext
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).debugMovies = {
        getCache: () => {
          console.log('DEBUG: Current movie data cache:', movieDataCache);
          console.log('DEBUG: Cache size:', movieDataCache.size);
          console.log('DEBUG: User reference:', userRef.current ? `${userRef.current.displayName} (${userRef.current.uid})` : 'null');
          console.log('DEBUG: Data loaded:', dataLoadedRef.current);
          console.log('DEBUG: Is loading:', isLoading);
          console.log('DEBUG: Cache version:', cacheVersion);
          return movieDataCache;
        },
        getCacheAsArray: () => {
          const cacheArray = Array.from(movieDataCache.entries());
          console.log('DEBUG: Cache as array:', cacheArray);
          return cacheArray;
        },
        getWatchlistMovies: () => {
          const watchlistIds = Array.from(movieDataCache.values())
            .filter(data => data.isInWatchlist)
            .map(data => data.movieId);
          console.log('DEBUG: Watchlist movie IDs:', watchlistIds);
          return watchlistIds;
        },
        getWatchedMovies: () => {
          const watchedIds = Array.from(movieDataCache.values())
            .filter(data => data.isWatched)
            .map(data => data.movieId);
          console.log('DEBUG: Watched movie IDs:', watchedIds);
          return watchedIds;
        },
        refreshAllData: async () => {
          console.log('DEBUG: Manual refresh all movie data triggered');
          if (userRef.current) {
            await loadInitialData();
          }
          console.log('DEBUG: Manual refresh completed');
        },
        checkFirebaseData: async () => {
          if (!userRef.current) {
            console.log('DEBUG: No user authenticated for movie data');
            return;
          }
          console.log('DEBUG: Checking raw Firebase movie data...');
          try {
            const [watchlistIds, watchedMovies] = await Promise.all([
              movieService.getUserWatchlistMovies(userRef.current),
              movieService.getUserWatchedMovies(userRef.current)
            ]);
            console.log('DEBUG: Raw Firebase watchlist IDs:', watchlistIds);
            console.log('DEBUG: Raw Firebase watched movies:', watchedMovies);
          } catch (error) {
            console.error('DEBUG: Error fetching Firebase movie data:', error);
          }
        },
        findDuplicates: () => {
          console.log('DEBUG: Searching for potential duplicate movie entries...');
          const cache = Array.from(movieDataCache.entries());
          const potentialDuplicates: Array<{
            normalizedId: string;
            entries: Array<{ movieId: string; movieData: UserMovieData }>;
          }> = [];

          // Group by potential movie identifiers (normalize imdb IDs)
          const movieGroups = new Map<string, Array<{ movieId: string; movieData: UserMovieData }>>();

          cache.forEach(([movieId, movieData]) => {
            // Extract potential identifiers
            let normalizedId = movieId;

            // If it starts with 'tt' it's an IMDb ID
            if (movieId.startsWith('tt')) {
              normalizedId = movieId;
            }
            // If it contains 'tmdb', normalize it (handles double-prefix like tmdb_tmdb_XXX)
            else if (movieId.startsWith('tmdb')) {
              let stripped = movieId;
              // Strip ALL tmdb prefixes
              while (stripped.startsWith('tmdb_') || stripped.startsWith('tmdb')) {
                if (stripped.startsWith('tmdb_')) {
                  stripped = stripped.substring(5);
                } else if (stripped.startsWith('tmdb')) {
                  stripped = stripped.substring(4);
                }
              }
              normalizedId = `tmdb_${stripped}`;
            }

            if (!movieGroups.has(normalizedId)) {
              movieGroups.set(normalizedId, []);
            }
            movieGroups.get(normalizedId)!.push({ movieId, movieData });
          });

          // Find groups with multiple entries
          movieGroups.forEach((group, normalizedId) => {
            if (group.length > 1) {
              console.log(`DEBUG: Found potential duplicates for ${normalizedId}:`, group);
              potentialDuplicates.push({ normalizedId, entries: group });
            }
          });

          if (potentialDuplicates.length === 0) {
            console.log('DEBUG: No duplicate entries found!');
          } else {
            console.log('DEBUG: Found', potentialDuplicates.length, 'potential duplicate groups:', potentialDuplicates);
          }

          return potentialDuplicates;
        },
        clearLocalStorage: () => {
          if (userRef.current) {
            localStorage.removeItem(`movieData_${userRef.current.uid}`);
            console.log('DEBUG: Cleared localStorage cache for user');
          }
        },
        cleanupDuplicates: async () => {
          console.log('DEBUG: Starting duplicate cleanup and ID migration...');
          const duplicates = (window as any).debugMovies.findDuplicates();

          // Also migrate any old format IDs
          setMovieDataCache(prevCache => {
            const newCache = new Map<string, UserMovieData>();
            let migrationCount = 0;

            prevCache.forEach((movieData, oldId) => {
              const newId = migrateMovieId(oldId);
              if (newId !== oldId) {
                migrationCount++;
                console.log(`DEBUG: Migrating movie ID from ${oldId} to ${newId}`);
              }

              // Use the migrated ID
              const updatedMovieData = { ...movieData, movieId: newId };

              // Check if this ID already exists in the new cache
              if (newCache.has(newId)) {
                // Merge the data
                const existingData = newCache.get(newId)!;
                const mergedData = {
                  ...existingData,
                  isInWatchlist: existingData.isInWatchlist || updatedMovieData.isInWatchlist,
                  isWatched: existingData.isWatched || updatedMovieData.isWatched,
                  isFavorite: existingData.isFavorite || updatedMovieData.isFavorite,
                  rating: updatedMovieData.rating || existingData.rating,
                  review: updatedMovieData.review || existingData.review,
                  watchedDate: updatedMovieData.watchedDate || existingData.watchedDate,
                  createdAt: existingData.createdAt && updatedMovieData.createdAt
                    ? (existingData.createdAt < updatedMovieData.createdAt ? existingData.createdAt : updatedMovieData.createdAt)
                    : existingData.createdAt || updatedMovieData.createdAt,
                  updatedAt: existingData.updatedAt && updatedMovieData.updatedAt
                    ? (existingData.updatedAt > updatedMovieData.updatedAt ? existingData.updatedAt : updatedMovieData.updatedAt)
                    : existingData.updatedAt || updatedMovieData.updatedAt,
                  movieId: newId
                };
                newCache.set(newId, mergedData);
                console.log(`DEBUG: Merged duplicate data for ${newId}`);
              } else {
                newCache.set(newId, updatedMovieData);
              }
            });

            console.log(`DEBUG: Migrated ${migrationCount} movie IDs to new format`);
            return newCache;
          });

          if (duplicates.length === 0) {
            console.log('DEBUG: No duplicates found to clean up');
          } else {
            console.log('DEBUG: Found', duplicates.length, 'duplicate groups to clean up');

            // Handle remaining duplicates (after migration)
            for (const duplicateGroup of duplicates) {
              const { normalizedId, entries } = duplicateGroup;
              console.log(`DEBUG: Processing duplicate group for ${normalizedId}:`, entries);

              // Choose the "best" entry (prioritize entries with more complete data)
              let bestEntry = entries[0];
              for (const entry of entries) {
                // Prefer entries that have more data (watched, rating, review)
                const bestScore = (bestEntry.movieData.isWatched ? 2 : 0) +
                  (bestEntry.movieData.rating ? 1 : 0) +
                  (bestEntry.movieData.review ? 1 : 0);
                const currentScore = (entry.movieData.isWatched ? 2 : 0) +
                  (entry.movieData.rating ? 1 : 0) +
                  (entry.movieData.review ? 1 : 0);

                if (currentScore > bestScore) {
                  bestEntry = entry;
                }
              }

              console.log(`DEBUG: Selected best entry for ${normalizedId}:`, bestEntry);

              // Merge all data into the best entry
              const mergedData = { ...bestEntry.movieData };
              for (const entry of entries) {
                if (entry.movieData.isInWatchlist) mergedData.isInWatchlist = true;
                if (entry.movieData.isWatched) mergedData.isWatched = true;
                if (entry.movieData.isFavorite) mergedData.isFavorite = true;
                if (entry.movieData.rating) mergedData.rating = entry.movieData.rating;
                if (entry.movieData.review) mergedData.review = entry.movieData.review;
                if (entry.movieData.watchedDate) mergedData.watchedDate = entry.movieData.watchedDate;
                // Keep the earliest creation date
                if (entry.movieData.createdAt && (!mergedData.createdAt || entry.movieData.createdAt < mergedData.createdAt)) {
                  mergedData.createdAt = entry.movieData.createdAt;
                }
                // Keep the latest update date
                if (entry.movieData.updatedAt && (!mergedData.updatedAt || entry.movieData.updatedAt > mergedData.updatedAt)) {
                  mergedData.updatedAt = entry.movieData.updatedAt;
                }
              }

              // Update the cache with the merged data using the normalized ID
              mergedData.movieId = normalizedId;
              setMovieDataCache(prevCache => {
                const newCache = new Map(prevCache);

                // Remove all old entries
                for (const entry of entries) {
                  newCache.delete(entry.movieId);
                }

                // Add the merged entry with normalized ID
                newCache.set(normalizedId, mergedData);

                console.log(`DEBUG: Merged ${entries.length} entries into single entry with ID ${normalizedId}`);
                return newCache;
              });
            }
          }

          // Update localStorage with cleaned cache
          setCacheVersion(v => v + 1);
          console.log('DEBUG: Duplicate cleanup and ID migration completed successfully');
        },
        isLoading: () => isLoading,
        dataLoaded: () => dataLoadedRef.current,
        forceReload: () => {
          console.log('DEBUG: Force reloading movie data...');
          dataLoadedRef.current = false;
          loadingRef.current = false;
          if (userRef.current) {
            loadInitialData();
          }
        },
        forceRefreshFromFirebase: async () => {
          console.log('DEBUG: Force refreshing from Firebase and clearing localStorage...');

          // Clear localStorage first
          if (userRef.current) {
            localStorage.removeItem(`movieData_${userRef.current.uid}`);
            console.log('DEBUG: Cleared localStorage cache');
          }

          // Clear current cache
          setMovieDataCache(new Map());
          setCacheVersion(v => v + 1);
          console.log('DEBUG: Cleared in-memory cache');

          // Reset loading state
          dataLoadedRef.current = false;
          loadingRef.current = false;

          // Reload fresh data from Firebase
          if (userRef.current) {
            console.log('DEBUG: Starting fresh load from Firebase...');
            await loadInitialData();
            console.log('DEBUG: Fresh load completed');
          }
        },
        verifyFirebaseDeletion: async () => {
          if (!userRef.current) {
            console.log('DEBUG: No user for deletion verification');
            return;
          }

          console.log('DEBUG: Verifying Firebase data deletion...');

          try {
            // Check all collections for remaining user data
            const [allMovieData, userPrefs, watchlists] = await Promise.all([
              movieService.getAllUserMovieData(userRef.current),
              authService.getUserPreferences(userRef.current),
              watchlistService.getUserWatchlists(userRef.current.uid),
            ]);

            console.log('DEBUG: Firebase verification results:');
            console.log('  - UserMovieData documents:', allMovieData.length);
            console.log('  - UserPreferences exists:', !!userPrefs);
            console.log('  - Watchlists count:', watchlists.length);

            if (allMovieData.length > 0) {
              console.log('DEBUG: Remaining movie data:', allMovieData);
            }
            if (userPrefs) {
              console.log('DEBUG: Remaining preferences:', userPrefs);
            }
            if (watchlists.length > 0) {
              console.log('DEBUG: Remaining watchlists:', watchlists);
            }

            const totalRemaining = allMovieData.length + (userPrefs ? 1 : 0) + watchlists.length;
            if (totalRemaining === 0) {
              console.log('DEBUG: ✅ All user data successfully deleted from Firebase!');
            } else {
              console.log('DEBUG: ❌ Some user data still exists in Firebase:', totalRemaining, 'items');
            }

            return {
              movieData: allMovieData,
              preferences: userPrefs,
              watchlists: watchlists,
              totalRemaining: totalRemaining
            };
          } catch (error) {
            console.error('DEBUG: Error verifying Firebase deletion:', error);
            return null;
          }
        },
        verifyDataConsistency: async () => {
          if (!userRef.current) {
            console.log('DEBUG: No user for data verification');
            return;
          }

          console.log('DEBUG: Starting data consistency verification...');

          try {
            // Get fresh data from Firebase
            const freshData = await movieService.getAllUserMovieData(userRef.current);
            const freshMap = new Map(freshData.map(d => [d.movieId, d]));

            console.log('DEBUG: Fresh Firebase data:', freshData.length, 'movies');
            console.log('DEBUG: Current cache data:', movieDataCache.size, 'movies');

            // Compare with current cache
            const missingInCache: string[] = [];
            const differentInCache: any[] = [];
            const extraInCache: string[] = [];

            // Check for missing or different data
            for (const [movieId, firebaseData] of Array.from(freshMap.entries())) {
              const cacheData = movieDataCache.get(movieId);
              if (!cacheData) {
                missingInCache.push(movieId);
              } else {
                // Check if data is different
                if (
                  cacheData.isInWatchlist !== firebaseData.isInWatchlist ||
                  cacheData.isWatched !== firebaseData.isWatched ||
                  cacheData.rating !== firebaseData.rating ||
                  cacheData.review !== firebaseData.review
                ) {
                  differentInCache.push({
                    movieId,
                    cache: {
                      isInWatchlist: cacheData.isInWatchlist,
                      isWatched: cacheData.isWatched,
                      rating: cacheData.rating,
                      hasReview: !!cacheData.review
                    },
                    firebase: {
                      isInWatchlist: firebaseData.isInWatchlist,
                      isWatched: firebaseData.isWatched,
                      rating: firebaseData.rating,
                      hasReview: !!firebaseData.review
                    }
                  });
                }
              }
            }

            // Check for extra data in cache
            for (const movieId of Array.from(movieDataCache.keys())) {
              if (!freshMap.has(movieId)) {
                extraInCache.push(movieId);
              }
            }

            console.log('DEBUG: Data consistency report:', {
              missingInCache: missingInCache.length,
              differentInCache: differentInCache.length,
              extraInCache: extraInCache.length,
              missingMovies: missingInCache,
              differentMovies: differentInCache,
              extraMovies: extraInCache
            });

            if (missingInCache.length === 0 && differentInCache.length === 0 && extraInCache.length === 0) {
              console.log('DEBUG: ✅ Data is consistent between cache and Firebase!');
            } else {
              console.log('DEBUG: ❌ Data inconsistencies found. Consider running forceRefreshFromFirebase()');
            }

            return {
              consistent: missingInCache.length === 0 && differentInCache.length === 0 && extraInCache.length === 0,
              missingInCache,
              differentInCache,
              extraInCache
            };
          } catch (error) {
            console.error('DEBUG: Error during data verification:', error);
            return null;
          }
        },
        checkWatchlistFiltering: () => {
          console.log('DEBUG: Checking watchlist filtering logic...');

          const allMovies = Array.from(movieDataCache.values());
          const watchlistMovies = allMovies.filter(movie => movie.isInWatchlist);
          const watchedMovies = allMovies.filter(movie => movie.isWatched);
          const wantToWatchMovies = allMovies.filter(movie => movie.isInWatchlist && !movie.isWatched);

          console.log('DEBUG: Movie categorization:');
          console.log('  - All movies in cache:', allMovies.length);
          console.log('  - In watchlist (isInWatchlist=true):', watchlistMovies.length);
          console.log('  - Watched (isWatched=true):', watchedMovies.length);
          console.log('  - Want to watch (isInWatchlist=true && isWatched=false):', wantToWatchMovies.length);

          console.log('DEBUG: Want to watch movies:', wantToWatchMovies.map(m => ({
            movieId: m.movieId,
            isInWatchlist: m.isInWatchlist,
            isWatched: m.isWatched
          })));

          console.log('DEBUG: Watched movies:', watchedMovies.map(m => ({
            movieId: m.movieId,
            isInWatchlist: m.isInWatchlist,
            isWatched: m.isWatched
          })));

          return {
            totalMovies: allMovies.length,
            watchlistCount: watchlistMovies.length,
            watchedCount: watchedMovies.length,
            wantToWatchCount: wantToWatchMovies.length,
            wantToWatchMovies: wantToWatchMovies.map(m => m.movieId),
            watchedMovies: watchedMovies.map(m => m.movieId)
          };
        },
        fixWatchedMoviesWatchlistStatus: async () => {
          console.log('DEBUG: Fixing watched movies that were incorrectly added to watchlist...');

          if (!userRef.current) {
            console.log('DEBUG: No user for fixing watched movies');
            return;
          }

          const allMovies = Array.from(movieDataCache.values());
          const watchedMovies = allMovies.filter(movie => movie.isWatched && movie.isInWatchlist);

          console.log('DEBUG: Found', watchedMovies.length, 'watched movies that are also in watchlist');

          // For each watched movie, check if it was originally in a watchlist before being watched
          // If not, remove it from the watchlist
          let fixedCount = 0;

          for (const movie of watchedMovies) {
            try {
              // Check if this movie was in a user's custom watchlist
              const userWatchlists = await watchlistService.getUserWatchlists(userRef.current.uid);
              let isInCustomWatchlist = false;

              for (const watchlist of userWatchlists) {
                if (watchlist.movieDetails.some(m => m.id === movie.movieId || m.imdbID === movie.movieId)) {
                  isInCustomWatchlist = true;
                  break;
                }
              }

              if (!isInCustomWatchlist) {
                // This movie was likely automatically added to watchlist when marked as watched
                // Remove it from the general watchlist but keep it as watched
                console.log(`DEBUG: Removing "${movie.movieId}" from watchlist (keeping as watched)`);

                const updatedData: UserMovieData = {
                  ...movie,
                  isInWatchlist: false, // Remove from general watchlist
                  updatedAt: new Date() as any,
                };

                // Update in Firebase
                await movieService.saveUserMovieData(userRef.current, movie.movieId, {
                  isInWatchlist: false
                });

                // Update in cache
                updateMovieDataInCache(movie.movieId, updatedData);
                fixedCount++;
              }
            } catch (error) {
              console.error(`DEBUG: Error fixing movie ${movie.movieId}:`, error);
            }
          }

          console.log(`DEBUG: Fixed ${fixedCount} watched movies that were incorrectly in watchlist`);
          setCacheVersion(v => v + 1); // Force UI refresh

          return {
            totalWatchedMovies: watchedMovies.length,
            fixedMovies: fixedCount,
            remainingInWatchlist: watchedMovies.length - fixedCount
          };
        },
        debugMovieStatus: (movieId: string) => {
          console.log(`DEBUG: Movie status for "${movieId}":`);
          const movieData = movieDataCache.get(movieId);

          if (!movieData) {
            console.log('  - No data found in cache');
            return null;
          }

          console.log('  - Movie data:', {
            isInWatchlist: movieData.isInWatchlist,
            isWatched: movieData.isWatched,
            rating: movieData.rating,
            hasReview: !!movieData.review,
            createdAt: movieData.createdAt,
            watchedDate: movieData.watchedDate,
            updatedAt: movieData.updatedAt
          });

          return movieData;
        },
        refreshSpecificMovie: async (movieId: string) => {
          console.log(`DEBUG: Refreshing movie data for "${movieId}"`);

          if (!userRef.current) {
            console.log('DEBUG: No user for refreshing movie');
            return;
          }

          try {
            // Fetch fresh data from Firebase
            const freshData = await movieService.getUserMovieData(userRef.current, movieId);

            if (freshData) {
              console.log(`DEBUG: Fresh data from Firebase:`, freshData);
              updateMovieDataInCache(movieId, freshData);
              console.log(`DEBUG: Updated cache for ${movieId}`);
            } else {
              console.log(`DEBUG: No data found in Firebase for ${movieId}`);
              // Remove from cache if it doesn't exist in Firebase
              removeMovieDataFromCache(movieId);
            }

            setCacheVersion(v => v + 1); // Force UI refresh

            return freshData;
          } catch (error) {
            console.error(`DEBUG: Error refreshing movie ${movieId}:`, error);
            return null;
          }
        },
        identifyAndFixDuplicates: async () => {
          console.log('DEBUG: Starting comprehensive duplicate identification and fixing...');

          if (!userRef.current) {
            console.log('DEBUG: No user for duplicate fixing');
            return;
          }

          const duplicates = (window as any).debugMovies.findDuplicates();
          let fixedCount = 0;
          let mergedCount = 0;

          for (const duplicateGroup of duplicates) {
            const { normalizedId, entries } = duplicateGroup;

            if (entries.length <= 1) continue;

            console.log(`DEBUG: Processing duplicate group for ${normalizedId} with ${entries.length} entries`);

            // Find the best entry (most complete data)
            let bestEntry = entries[0];
            let bestScore = 0;

            for (const entry of entries) {
              let score = 0;

              // Score based on data completeness
              if (entry.movieData.isWatched) score += 10;
              if (entry.movieData.rating) score += 5;
              if (entry.movieData.review) score += 5;
              if (entry.movieData.isInWatchlist) score += 3;
              if (entry.movieData.watchedDate) score += 2;

              // Prefer proper IMDb IDs
              if (entry.movieId.startsWith('tt')) score += 20;
              else if (entry.movieId.startsWith('tmdb_')) score += 10;

              if (score > bestScore) {
                bestScore = score;
                bestEntry = entry;
              }
            }

            console.log(`DEBUG: Selected best entry for ${normalizedId}:`, bestEntry, `(score: ${bestScore})`);

            // Merge all data into the best entry
            const mergedData = { ...bestEntry.movieData };
            for (const entry of entries) {
              if (entry.movieData.isInWatchlist) mergedData.isInWatchlist = true;
              if (entry.movieData.isWatched) mergedData.isWatched = true;
              if (entry.movieData.isFavorite) mergedData.isFavorite = true;
              if (entry.movieData.rating && !mergedData.rating) mergedData.rating = entry.movieData.rating;
              if (entry.movieData.review && !mergedData.review) mergedData.review = entry.movieData.review;
              if (entry.movieData.watchedDate && !mergedData.watchedDate) mergedData.watchedDate = entry.movieData.watchedDate;
              // Keep the earliest creation date
              if (entry.movieData.createdAt && (!mergedData.createdAt || entry.movieData.createdAt < mergedData.createdAt)) {
                mergedData.createdAt = entry.movieData.createdAt;
              }
            }

            try {
              // Update the best entry with merged data
              const finalId = getStandardizedMovieId({
                imdbID: bestEntry.movieId,
                id: bestEntry.movieId,
                Title: 'Unknown'
              });

              mergedData.movieId = finalId;
              mergedData.updatedAt = new Date() as any;

              // Save merged data to Firebase
              await movieService.saveUserMovieData(userRef.current, finalId, mergedData);

              // Remove all other entries from Firebase and cache
              for (const entry of entries) {
                if (entry.movieId !== finalId) {
                  try {
                    await movieService.deleteUserMovieData(userRef.current, entry.movieId);
                    removeMovieDataFromCache(entry.movieId);
                    console.log(`DEBUG: Removed duplicate entry ${entry.movieId}`);
                  } catch (error) {
                    console.warn(`DEBUG: Error removing duplicate ${entry.movieId}:`, error);
                  }
                }
              }

              // Update cache with merged data
              updateMovieDataInCache(finalId, mergedData);
              fixedCount++;
              mergedCount += entries.length - 1;

              console.log(`DEBUG: Successfully merged ${entries.length} entries into ${finalId}`);

            } catch (error) {
              console.error(`DEBUG: Error fixing duplicate group ${normalizedId}:`, error);
            }
          }

          console.log(`DEBUG: Duplicate fixing completed. Fixed ${fixedCount} groups, merged ${mergedCount} entries`);
          setCacheVersion(v => v + 1); // Force UI refresh

          return {
            duplicateGroups: duplicates.length,
            fixedGroups: fixedCount,
            mergedEntries: mergedCount
          };
        }
      };
    }
  }, [movieDataCache, isLoading, cacheVersion]);

  // Load from localStorage as fallback
  const loadFromLocalStorage = useCallback(() => {
    if (!userRef.current) return new Map();

    try {
      const stored = localStorage.getItem(`movieData_${userRef.current.uid}`);
      if (stored) {
        const cacheArray = JSON.parse(stored);
        const restoredCache = new Map(cacheArray);
        console.log('UserMovieDataContext: Restored from localStorage:', restoredCache.size, 'items');
        return restoredCache;
      }
    } catch (error) {
      console.error('UserMovieDataContext: Error loading from localStorage:', error);
    }

    return new Map();
  }, []);

  // Memoize the loadInitialData function to prevent unnecessary re-renders
  const loadInitialData = useCallback(async () => {
    const currentUser = userRef.current;
    if (!currentUser) {
      console.log('UserMovieDataContext: No user provided, setting loading to false');
      setIsLoading(false);
      dataLoadedRef.current = false;
      loadingRef.current = false;
      return;
    }

    // Check if Firebase is properly initialized before attempting operations
    // Note: The effect now depends on isFirebaseReady, so this is a safety check
    // that should rarely trigger. Keep loading state true if this check fails.
    if (!isFirebaseInitialized()) {
      console.log('UserMovieDataContext: Firebase not yet initialized (safety check), keeping loading state');
      loadingRef.current = false;
      // Don't set isLoading to false - keep loading UI while waiting for Firebase
      return;
    }

    // Prevent concurrent loads
    if (loadingRef.current) {
      console.log('UserMovieDataContext: Load already in progress, skipping');
      return;
    }

    loadingRef.current = true;

    console.log('UserMovieDataContext: Loading initial data for user:', currentUser.uid);

    // Only load from localStorage if we haven't just cleared data
    // Check if this is a force reload (dataLoadedRef.current was reset to false)
    const localCache = loadFromLocalStorage();
    if (localCache.size > 0 && dataLoadedRef.current) {
      console.log('UserMovieDataContext: Using localStorage cache while loading from Firebase:', localCache.size, 'items');
      setMovieDataCache(localCache);
      setCacheVersion(v => v + 1);
      setIsLoading(false); // Hide loading state since we have cached data
    } else {
      console.log('UserMovieDataContext: Skipping localStorage cache - loading fresh from Firebase');
      setIsLoading(true);
    }

    try {
      console.log('UserMovieDataContext: Fetching ALL user movie data from Firebase...');

      // Load ALL user movie data from Firebase (not just watchlist and watched)
      // This includes movies in custom watchlists, rated movies, etc.

      // Add timeout to prevent infinite loading - resolve with empty array instead of rejecting
      // Use longer timeout for iOS native Firestore (slower than web)
      // Using centralized platform detection for consistency
      const mainQueryTimeout = getFirestoreQueryTimeout();
      console.log(`UserMovieDataContext: Using ${mainQueryTimeout}ms query timeout (native: ${isCapacitorNative()})`);

      let mainRaceTimer: ReturnType<typeof setTimeout> | undefined;
      const timeoutPromise = new Promise<UserMovieData[]>((resolve) => {
        mainRaceTimer = setTimeout(() => {
          console.warn(`UserMovieDataContext: Firebase query timeout after ${mainQueryTimeout / 1000} seconds, continuing with empty data`);
          resolve([]);
        }, mainQueryTimeout);
      });

      // Get all user movie data with timeout
      let allUserMovieData: UserMovieData[];
      try {
        allUserMovieData = await Promise.race([
          movieService.getAllUserMovieData(currentUser),
          timeoutPromise
        ]);
      } finally {
        if (mainRaceTimer !== undefined) clearTimeout(mainRaceTimer);
      }

      console.log('UserMovieDataContext: Got ALL movie data from Firebase:', allUserMovieData.length, 'movies');

      const newCache = new Map<string, UserMovieData>();

      // Add all user movie data to cache
      // Store data under BOTH the original ID and the standardized ID to ensure lookups work
      for (const movieData of allUserMovieData) {
        const originalId = movieData.movieId;

        // Store with original ID for backward compatibility
        newCache.set(originalId, movieData);

        // Also store with standardized ID if it's different
        // This ensures lookups using getStandardizedMovieId() will find the data
        const standardizedId = migrateMovieId(originalId);
        if (standardizedId !== originalId && !newCache.has(standardizedId)) {
          // Create a copy with the standardized ID
          const standardizedData = { ...movieData, movieId: standardizedId };
          newCache.set(standardizedId, standardizedData);
        }

        console.log('UserMovieDataContext: Added movie to cache:', originalId, {
          isInWatchlist: movieData.isInWatchlist,
          isWatched: movieData.isWatched,
          rating: movieData.rating,
          hasReview: !!movieData.review
        });
      }

      // Also get movies from user's custom watchlists (in case they're not in userMovies collection yet)
      try {
        // Add timeout for watchlists query too - using centralized platform detection
        const watchlistQueryTimeout = getWatchlistQueryTimeout();

        let watchlistRaceTimer: ReturnType<typeof setTimeout> | undefined;
        const watchlistTimeoutPromise = new Promise<any[]>((resolve) => {
          watchlistRaceTimer = setTimeout(() => {
            console.warn(`UserMovieDataContext: Watchlists query timeout after ${watchlistQueryTimeout / 1000} seconds`);
            resolve([]);
          }, watchlistQueryTimeout);
        });

        let userWatchlists: any[];
        try {
          userWatchlists = await Promise.race([
            watchlistService.getUserWatchlists(currentUser.uid),
            watchlistTimeoutPromise
          ]);
        } finally {
          if (watchlistRaceTimer !== undefined) clearTimeout(watchlistRaceTimer);
        }
        console.log('UserMovieDataContext: Found', userWatchlists.length, 'user watchlists');

        // Collect unique collaborator IDs to fetch their names
        const collaboratorIds = new Set<string>();
        for (const watchlist of userWatchlists) {
          for (const movie of watchlist.movieDetails) {
            if (movie.addedBy && movie.addedBy !== currentUser.uid) {
              collaboratorIds.add(movie.addedBy);
            }
          }
        }

        // Fetch collaborator profiles in batch
        const collaboratorNames = new Map<string, string>();
        if (collaboratorIds.size > 0) {
          const profilePromises = Array.from(collaboratorIds).map(async (uid) => {
            try {
              const profile = await getCachedUserProfile(uid);
              if (profile?.displayName) {
                collaboratorNames.set(uid, profile.displayName);
              }
            } catch (error) {
              console.warn('UserMovieDataContext: Failed to fetch collaborator profile:', uid);
            }
          });
          await Promise.all(profilePromises);
        }

        for (const watchlist of userWatchlists) {
          for (const movie of watchlist.movieDetails) {
            const movieId = movie.id || movie.imdbID;
            // Only add if movieId exists and not already in cache (to avoid overwriting more complete data)
            if (movieId && !newCache.has(movieId)) {
              // Use the actual addedAt timestamp from the watchlist, not current time
              // This ensures "Recently added" section shows truly recent additions
              const addedAtDate = movie.addedAt ? new Date(movie.addedAt) : new Date();

              // Check if movie was added by a collaborator (not the current user)
              const isAddedByCollaborator = movie.addedBy && movie.addedBy !== currentUser.uid;

              const minimalData: UserMovieData = {
                uid: currentUser.uid,
                movieId,
                isInWatchlist: true, // Since it's in a custom watchlist
                isWatched: false,
                isFavorite: false,
                watchCount: 0,
                createdAt: addedAtDate as any,
                updatedAt: addedAtDate as any,
                ...(isAddedByCollaborator && {
                  addedByCollaboratorId: movie.addedBy,
                  addedByCollaboratorName: collaboratorNames.get(movie.addedBy!) || undefined,
                }),
              };
              newCache.set(movieId, minimalData);
              console.log('UserMovieDataContext: Added movie from custom watchlist:', movieId, watchlist.name, 'addedAt:', movie.addedAt, isAddedByCollaborator ? `addedBy: ${collaboratorNames.get(movie.addedBy!) || movie.addedBy}` : '');
            }
          }
        }
      } catch (error) {
        console.error('UserMovieDataContext: Error loading custom watchlists:', error);
      }

      console.log('UserMovieDataContext: Created comprehensive cache with', newCache.size, 'movies');
      console.log('UserMovieDataContext: Cache breakdown:', {
        watchlistMovies: Array.from(newCache.values()).filter(m => m.isInWatchlist).length,
        watchedMovies: Array.from(newCache.values()).filter(m => m.isWatched).length,
        ratedMovies: Array.from(newCache.values()).filter(m => m.rating).length,
        reviewedMovies: Array.from(newCache.values()).filter(m => m.review).length,
      });

      // Update cache with fresh data from Firebase
      setMovieDataCache(newCache);
      setCacheVersion(v => v + 1); // Only increment once for initial load

      // Run comprehensive ID migration after cache is loaded
      setTimeout(() => {
        performComprehensiveIdMigration();
      }, 1000); // Small delay to ensure cache is fully set

      // Update localStorage backup with debouncing
      try {
        const cacheArray = Array.from(newCache.entries());
        setDebouncedItem(`movieData_${currentUser.uid}`, JSON.stringify(cacheArray));
        console.log('UserMovieDataContext: Updated localStorage backup with comprehensive data:', cacheArray.length, 'items');
      } catch (error) {
        console.error('UserMovieDataContext: Error updating localStorage backup:', error);
      }

      dataLoadedRef.current = true;
    } catch (error: any) {
      console.error('UserMovieDataContext: Error loading comprehensive user movie data:', error);
      console.error('UserMovieDataContext: Error details:', {
        message: error?.message,
        code: error?.code,
        stack: error?.stack?.substring(0, 300)
      });

      // If Firebase query times out or fails, initialize with empty cache
      // This prevents the app from being stuck in loading state
      console.log('UserMovieDataContext: Initializing with empty cache due to error');
      setMovieDataCache(new Map());
      setCacheVersion(v => v + 1);

      // If Firebase fails but we have localStorage data, keep using it
      if (localCache.size > 0) {
        console.log('UserMovieDataContext: Firebase failed but keeping localStorage data');
        setMovieDataCache(localCache);
        setCacheVersion(v => v + 1);
        dataLoadedRef.current = true;
      }
    } finally {
      console.log('UserMovieDataContext: Load complete, setting isLoading to false');
      setIsLoading(false);
      loadingRef.current = false;
    }
  }, [loadFromLocalStorage, setDebouncedItem]);

  // Initialize and load user movie data - but only when user actually changes OR when Firebase becomes ready
  // This is critical for iOS where Firebase may not be ready when the user is first set
  useEffect(() => {
    // Debug logging for iOS loading issues
    console.log('[UserMovieDataContext] Init effect:', {
      hasUser: !!user,
      userId: user?.uid?.slice(-6) || 'none',
      isFirebaseReady,
      dataLoaded: dataLoadedRef.current,
      isLoadingRef: loadingRef.current,
      currentIsLoading: isLoading
    });

    if (user && isFirebaseReady && !dataLoadedRef.current && !loadingRef.current) {
      console.log('[UserMovieDataContext] Loading initial data...');
      loadInitialData();
    } else if (user && !isFirebaseReady) {
      console.log('[UserMovieDataContext] Waiting for Firebase...');
      // Keep loading state true to show loading UI while waiting for Firebase
      setIsLoading(true);
    } else if (!user) {
      console.log('[UserMovieDataContext] No user, clearing');
      setMovieDataCache(new Map());
      setIsLoading(false);
      dataLoadedRef.current = false;
      loadingRef.current = false;
    } else {
      console.log('[UserMovieDataContext] Skipped - dataLoaded:', dataLoadedRef.current, 'loadingRef:', loadingRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, isFirebaseReady]); // Re-run when user UID changes OR when Firebase becomes ready

  const getUserMovieData = useCallback((movieId: string): UserMovieData | null => {
    // First try direct lookup
    let movieData = movieDataCache.get(movieId);

    // If not found, try alternate ID formats
    // This handles legacy data where IDs were stored without the 'tmdb_' prefix
    // Also handles double-prefixed IDs like tmdb_tmdb_XXX
    if (!movieData && movieId) {
      // Helper to extract numeric ID from any tmdb format
      const extractNumericId = (id: string): string => {
        let normalized = id;
        // Strip ALL tmdb prefixes (handles double-prefix like tmdb_tmdb_XXX)
        while (normalized.startsWith('tmdb_') || normalized.startsWith('tmdb')) {
          if (normalized.startsWith('tmdb_')) {
            normalized = normalized.substring(5);
          } else if (normalized.startsWith('tmdb')) {
            normalized = normalized.substring(4);
          }
        }
        return normalized;
      };

      // If it contains 'tmdb', extract the numeric part and try variations
      if (movieId.startsWith('tmdb')) {
        const numericId = extractNumericId(movieId);
        const normalizedId = `tmdb_${numericId}`;

        // Try the normalized format first
        if (normalizedId !== movieId) {
          movieData = movieDataCache.get(normalizedId);
          if (movieData) {
            console.log(`UserMovieDataContext: Found "${movieId}" using normalized key "${normalizedId}"`);
          }
        }

        // Try just the numeric ID
        if (!movieData) {
          movieData = movieDataCache.get(numericId);
          if (movieData) {
            console.log(`UserMovieDataContext: Found "${movieId}" using alternate key "${numericId}"`);
          }
        }
      }
      // If looking for just a number, also check for tmdb_XXX
      else if (/^\d+$/.test(movieId)) {
        const prefixedId = `tmdb_${movieId}`;
        movieData = movieDataCache.get(prefixedId);
        if (movieData) {
          console.log(`UserMovieDataContext: Found "${movieId}" using alternate key "${prefixedId}"`);
        }
      }
    }

    // Reduced logging for performance - only log when data is not found for valid IDs
    if (movieId && movieId !== 'unknown' && !movieData) {
      console.log(`UserMovieDataContext: No data found for "${movieId}" (cache size: ${movieDataCache.size})`);
    }

    return movieData || null;
  }, [movieDataCache]);

  const updateMovieDataInCache = useCallback((movieId: string, data: UserMovieData) => {
    console.log('UserMovieDataContext: updateMovieDataInCache called with:', movieId, data);
    setMovieDataCache(prevCache => {
      const newCache = new Map(prevCache);
      newCache.set(movieId, data);
      console.log('UserMovieDataContext: Cache updated, new size:', newCache.size);

      // Backup to localStorage for persistence with debouncing
      if (userRef.current) {
        try {
          const cacheArray = Array.from(newCache.entries());
          setDebouncedItem(`movieData_${userRef.current.uid}`, JSON.stringify(cacheArray));
          console.log('UserMovieDataContext: Backed up to localStorage:', cacheArray.length, 'items');
        } catch (error) {
          console.error('UserMovieDataContext: Error backing up to localStorage:', error);
        }
      }

      return newCache;
    });

    // Batch cache version updates to reduce re-renders
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    updateTimeoutRef.current = setTimeout(() => {
      setCacheVersion(v => {
        const newVersion = v + 1;
        console.log('UserMovieDataContext: Cache version incremented to:', newVersion);
        return newVersion;
      });
      updateTimeoutRef.current = null;
    }, 50); // 50ms debounce
  }, [setDebouncedItem]);

  const removeMovieDataFromCache = useCallback((movieId: string) => {
    console.log('UserMovieDataContext: removeMovieDataFromCache called with:', movieId);
    setMovieDataCache(prevCache => {
      const newCache = new Map(prevCache);
      const removed = newCache.delete(movieId);
      console.log('UserMovieDataContext: Movie removed from cache:', removed, 'new size:', newCache.size);

      // Update localStorage backup with debouncing
      if (userRef.current) {
        try {
          const cacheArray = Array.from(newCache.entries());
          setDebouncedItem(`movieData_${userRef.current.uid}`, JSON.stringify(cacheArray));
          console.log('UserMovieDataContext: Updated localStorage backup:', cacheArray.length, 'items');
        } catch (error) {
          console.error('UserMovieDataContext: Error updating localStorage backup:', error);
        }
      }

      return newCache;
    });

    // Batch cache version updates to reduce re-renders
    if (removeTimeoutRef.current) {
      clearTimeout(removeTimeoutRef.current);
    }
    removeTimeoutRef.current = setTimeout(() => {
      setCacheVersion(v => {
        const newVersion = v + 1;
        console.log('UserMovieDataContext: Cache version incremented to:', newVersion);
        return newVersion;
      });
      removeTimeoutRef.current = null;
    }, 50); // 50ms debounce
  }, [setDebouncedItem]);

  const updateMovieData = useCallback(async (movieId: string, data: Partial<UserMovieData>) => {
    const currentUser = userRef.current;
    if (!currentUser) return;

    try {
      await movieService.saveUserMovieData(currentUser, movieId, data);

      // Get fresh data from Firebase to ensure we have the latest state and prevent duplicates
      const freshData = await movieService.getUserMovieData(currentUser, movieId);
      if (freshData) {
        console.log('UserMovieDataContext: Updating cache with fresh Firebase data for updateMovieData:', freshData);
        updateMovieDataInCache(movieId, freshData);
      } else {
        // Fallback to local update if Firebase fetch fails
        const existingData = movieDataCache.get(movieId);
        const updatedData: UserMovieData = {
          uid: currentUser.uid,
          movieId,
          isInWatchlist: false,
          isWatched: false,
          isFavorite: false,
          watchCount: 0,
          createdAt: existingData?.createdAt || new Date() as any,
          updatedAt: new Date() as any,
          ...existingData,
          ...data,
        };

        console.log('UserMovieDataContext: Using fallback local update for updateMovieData:', updatedData);
        updateMovieDataInCache(movieId, updatedData);
      }
      if (data.isWatched !== undefined || data.rating !== undefined) {
        invalidateMoviePersonalityCache();
      }
    } catch (error) {
      console.error('Error updating movie data:', error);
      throw error;
    }
  }, [updateMovieDataInCache, invalidateMoviePersonalityCache]);

  // Helper function to find existing movie entries by title/year to detect duplicates
  const findExistingMovieEntry = useCallback((standardizedId: string, movieTitle: string, movieYear?: string): { id: string; data: UserMovieData } | null => {
    // First check if standardized ID exists
    const directMatch = movieDataCache.get(standardizedId);
    if (directMatch) {
      return { id: standardizedId, data: directMatch };
    }

    // Check for potential duplicates by matching title and year
    const normalizedTitle = movieTitle.toLowerCase().trim().replace(/[^a-z0-9]/g, '');
    const normalizedYear = movieYear?.trim();

    for (const [cachedId, cachedData] of Array.from(movieDataCache.entries())) {
      // Skip if it's the same ID we're checking
      if (cachedId === standardizedId) continue;

      // Try to extract title from cached movie data or ID
      let cachedTitle = '';
      if (cachedId.startsWith('title_')) {
        // Extract title from title-based ID
        const parts = cachedId.split('_');
        cachedTitle = parts.slice(1, -1).join('_'); // Remove 'title_' prefix and year suffix
      }

      // If we can match by title and year, this is likely a duplicate
      if (cachedTitle) {
        const cachedNormalizedTitle = cachedTitle.replace(/[^a-z0-9]/g, '');
        if (cachedNormalizedTitle === normalizedTitle) {
          // Check year if available
          if (normalizedYear) {
            const cachedYear = cachedId.split('_').pop();
            if (cachedYear === normalizedYear) {
              console.log('UserMovieDataContext: Found potential duplicate by title/year:', { cachedId, standardizedId, title: movieTitle });
              return { id: cachedId, data: cachedData };
            }
          } else {
            // If no year to compare, assume it's a match based on title
            console.log('UserMovieDataContext: Found potential duplicate by title only:', { cachedId, standardizedId, title: movieTitle });
            return { id: cachedId, data: cachedData };
          }
        }
      }
    }

    return null;
  }, [movieDataCache]);

  const addToWatchlist = useCallback(async (movieId: string, movieTitle: string, moviePoster?: string, movieYear?: string, shareToFeed: boolean = true) => {
    console.log('UserMovieDataContext: addToWatchlist called with:', { movieId, movieTitle, user: userRef.current ? `${userRef.current.displayName} (${userRef.current.uid})` : 'null' });
    const currentUser = userRef.current;
    if (!currentUser) {
      console.log('UserMovieDataContext: No user found for addToWatchlist - user is null!');
      return;
    }

    try {
      // First standardize the movie ID to prevent duplicates
      const standardizedId = getStandardizedMovieId({
        imdbID: movieId,
        id: movieId.startsWith('tmdb_') ? movieId.substring(5) : movieId,
        Title: movieTitle,
        Year: movieYear
      });

      console.log('UserMovieDataContext: Standardized movie ID:', { originalId: movieId, standardizedId });

      // Check if movie already exists in cache with any variant of the ID
      const existingEntry = findExistingMovieEntry(standardizedId, movieTitle, movieYear);
      if (existingEntry) {
        console.log('UserMovieDataContext: Movie already exists, updating existing entry:', existingEntry);

        // If the existing entry has a different ID, we need to migrate it
        if (existingEntry.id !== standardizedId) {
          console.log('UserMovieDataContext: Migrating movie ID from', existingEntry.id, 'to', standardizedId);

          // Remove old entry from cache and Firebase
          removeMovieDataFromCache(existingEntry.id);
          await movieService.deleteUserMovieData(currentUser, existingEntry.id);

          // Save with standardized ID
          await movieService.addToWatchlist(currentUser, standardizedId, movieTitle, moviePoster, movieYear, shareToFeed);

          // Get fresh data from Firebase to get the correct createdAt timestamp
          const freshData = await movieService.getUserMovieData(currentUser, standardizedId);
          if (freshData) {
            updateMovieDataInCache(standardizedId, freshData);
          }
        } else {
          // Update existing entry
          await movieService.addToWatchlist(currentUser, standardizedId, movieTitle, moviePoster, movieYear, shareToFeed);

          // Get fresh data from Firebase to ensure we have the latest createdAt timestamp
          const freshData = await movieService.getUserMovieData(currentUser, standardizedId);
          if (freshData) {
            updateMovieDataInCache(standardizedId, freshData);
          }
        }

        console.log('UserMovieDataContext: Updated existing movie entry');
        return;
      }

      console.log('UserMovieDataContext: Adding new movie to watchlist...');
      await movieService.addToWatchlist(currentUser, standardizedId, movieTitle, moviePoster, movieYear, shareToFeed);

      // Get fresh data from Firebase to ensure we have the latest state including correct createdAt timestamp
      const freshData = await movieService.getUserMovieData(currentUser, standardizedId);
      if (freshData) {
        console.log('UserMovieDataContext: Updating cache with fresh Firebase data for addToWatchlist:', freshData);
        updateMovieDataInCache(standardizedId, freshData);
      } else {
        // Fallback to local update if Firebase fetch fails
        const existingData = movieDataCache.get(standardizedId);
        const updatedData: UserMovieData = {
          uid: currentUser.uid,
          movieId: standardizedId,
          isInWatchlist: true,
          isWatched: existingData?.isWatched || false,
          isFavorite: existingData?.isFavorite || false,
          watchCount: existingData?.watchCount || 0,
          rating: existingData?.rating,
          review: existingData?.review,
          watchedDate: existingData?.watchedDate,
          createdAt: existingData?.createdAt || new Date() as any,
          updatedAt: new Date() as any,
        };

        console.log('UserMovieDataContext: Using fallback local update for addToWatchlist:', updatedData);
        updateMovieDataInCache(standardizedId, updatedData);
      }
      console.log('UserMovieDataContext: addToWatchlist completed successfully');
    } catch (error) {
      console.error('UserMovieDataContext: Error adding to watchlist:', error);
      throw error;
    }
  }, [movieDataCache, updateMovieDataInCache, removeMovieDataFromCache, findExistingMovieEntry]);

  const removeFromWatchlist = useCallback(async (movieId: string) => {
    const currentUser = userRef.current;
    if (!currentUser) return;

    try {
      await movieService.removeFromWatchlist(currentUser, movieId);

      // Update cache
      const existingData = movieDataCache.get(movieId);
      if (existingData) {
        if (existingData.isWatched) {
          // Keep the data but mark as not in watchlist
          const updatedData: UserMovieData = {
            ...existingData,
            isInWatchlist: false,
            updatedAt: new Date() as any,
          };
          updateMovieDataInCache(movieId, updatedData);
        } else {
          // Remove completely if not watched
          removeMovieDataFromCache(movieId);
        }
      }
    } catch (error) {
      console.error('Error removing from watchlist:', error);
      throw error;
    }
  }, [movieDataCache, updateMovieDataInCache, removeMovieDataFromCache]);

  const markAsWatched = useCallback(async (
    movieId: string,
    movieTitle: string,
    rating?: number,
    review?: string,
    isPrivate?: boolean,
    moviePoster?: string,
    movieYear?: string,
    visibility?: 'anyone' | 'only-me',
    watchedDateOverride?: Date | string | null,
    watchedDatePrecision?: 'day' | 'month' | 'year',
    shareToFeed: boolean = true
  ) => {
    console.log('UserMovieDataContext: markAsWatched called with:', {
      movieId,
      movieTitle,
      rating,
      review,
      visibility,
      watchedDateOverride: watchedDateOverride instanceof Date ? watchedDateOverride.toISOString() : watchedDateOverride,
      user: userRef.current ? `${userRef.current.displayName} (${userRef.current.uid})` : 'null'
    });

    // ✅ Data validation: Ensure consistency (any movie with rating/review must be watched)
    if ((rating && rating > 0) || (review && review.trim && review.trim().length > 0)) {
      console.log('UserMovieDataContext: Movie has rating/review, ensuring it is properly marked as watched');
    }

    const currentUser = userRef.current;
    if (!currentUser) {
      console.log('UserMovieDataContext: No user found for markAsWatched - user is null!');
      return;
    }

    try {
      const toDate = (value: unknown): Date | null => {
        if (!value) return null;
        if (value instanceof Date) {
          return Number.isNaN(value.getTime()) ? null : value;
        }
        if (typeof value === 'string') {
          // Handle native Timestamp string format: "Timestamp(seconds=X, nanoseconds=Y)"
          const match = value.match(/Timestamp\(seconds=(\d+),\s*nanoseconds=\d+\)/);
          if (match) {
            return new Date(parseInt(match[1], 10) * 1000);
          }
          const parsed = new Date(value);
          return Number.isNaN(parsed.getTime()) ? null : parsed;
        }
        if (typeof value === 'object' && value !== null) {
          const maybeHasToDate = value as { toDate?: () => Date; seconds?: number; _seconds?: number };
          if (typeof maybeHasToDate.toDate === 'function') {
            const converted = maybeHasToDate.toDate();
            return Number.isNaN(converted.getTime()) ? null : converted;
          }
          // Handle both {seconds} and {_seconds} formats from Capacitor
          const seconds = maybeHasToDate.seconds ?? maybeHasToDate._seconds;
          if (typeof seconds === 'number') {
            const converted = new Date(seconds * 1000);
            return Number.isNaN(converted.getTime()) ? null : converted;
          }
        }
        return null;
      };

      // Create immediate local update for UI responsiveness
      const existingData = movieDataCache.get(movieId);
      const normalizedOverride = toDate(watchedDateOverride);
      const existingWatchedDate = toDate(existingData?.watchedDate as any);
      const effectiveWatchedDate = normalizedOverride || existingWatchedDate || new Date();
      const watchedDateForService = normalizedOverride || existingWatchedDate || undefined;

      const immediateUpdatedData: UserMovieData = {
        uid: currentUser.uid,
        movieId,
        isInWatchlist: false, // Always remove from "want to watch" when marking as watched
        isWatched: true,
        isFavorite: existingData?.isFavorite || false,
        watchCount: (existingData?.watchCount || 0) + 1,
        rating,
        review: review?.trim() || undefined,
        reviewVisibility: visibility || 'anyone',
        watchedDate: effectiveWatchedDate as any,
        watchedDatePrecision,
        createdAt: existingData?.createdAt || new Date() as any,
        updatedAt: new Date() as any,
      };

      // Update local cache FIRST for immediate UI responsiveness
      updateMovieDataInCache(movieId, immediateUpdatedData);
      console.log('UserMovieDataContext: Updated local cache immediately for markAsWatched - removed from want-to-watch');

      // Force immediate cache version update for critical operations
      setCacheVersion(v => v + 1);
      console.log('UserMovieDataContext: Forced immediate cache version update for markAsWatched');

      console.log('UserMovieDataContext: Calling Firebase markAsWatched...');
      await movieService.markAsWatched(
        currentUser,
        movieId,
        movieTitle,
        rating,
        review,
        isPrivate,
        moviePoster,
        movieYear,
        visibility,
        watchedDateForService,
        watchedDatePrecision,
        shareToFeed
      );

      // Get fresh data from Firebase to ensure we have the latest state and sync any differences
      const freshData = await movieService.getUserMovieData(currentUser, movieId);
      if (freshData) {
        console.log('UserMovieDataContext: Syncing with fresh Firebase data:', freshData);

        // Ensure isInWatchlist is false (business rule: watched movies not in want-to-watch)
        const syncedData = {
          ...freshData,
          isInWatchlist: false, // Always enforce this business rule
        };

        console.log('UserMovieDataContext: Final sync - ensuring movie is removed from want-to-watch');
        updateMovieDataInCache(movieId, syncedData);
      }

      console.log('UserMovieDataContext: markAsWatched completed successfully');
      invalidateMoviePersonalityCache();
    } catch (error) {
      console.error('UserMovieDataContext: Error marking as watched:', error);
      throw error;
    }
  }, [movieDataCache, updateMovieDataInCache, invalidateMoviePersonalityCache]);

  const unmarkAsWatched = useCallback(async (movieId: string) => {
    const currentUser = userRef.current;
    if (!currentUser) return;

    try {
      await movieService.unmarkAsWatched(currentUser, movieId);

      // Update cache - now also removes rating, review, and visibility
      const existingData = movieDataCache.get(movieId);
      if (existingData) {
        const updatedData: UserMovieData = {
          ...existingData,
          isWatched: false,
          rating: undefined,
          review: undefined,
          reviewVisibility: undefined, // Clear review visibility when unwatching
          watchedDate: undefined,
          updatedAt: new Date() as any,
        };
        updateMovieDataInCache(movieId, updatedData);

        // Cache version will be updated by updateMovieDataInCache (debounced)
      }
      invalidateMoviePersonalityCache();
    } catch (error) {
      console.error('Error unmarking as watched:', error);
      throw error;
    }
  }, [movieDataCache, updateMovieDataInCache, invalidateMoviePersonalityCache]);

  // Delete rating and review completely and unwatch the movie
  const deleteRatingAndReview = useCallback(async (movieId: string) => {
    const currentUser = userRef.current;
    if (!currentUser) {
      console.log('UserMovieDataContext: No user found for deleteRatingAndReview');
      return;
    }

    console.log('UserMovieDataContext: Completely removing rating/review and unwatching movie');

    try {
      // Get existing data to check current state
      const existingData = movieDataCache.get(movieId);

      if (existingData) {
        const isInWatchlist = existingData.isInWatchlist;

        if (isInWatchlist) {
          // Keep in cache but remove all watched/review data
          const cleanedData: UserMovieData = {
            uid: currentUser.uid,
            movieId,
            isInWatchlist: true, // Keep in watchlist
            isWatched: false, // Unwatch the movie
            isFavorite: existingData.isFavorite || false,
            tags: existingData.tags || [],
            notes: existingData.notes,
            watchCount: 0, // Reset watch count
            createdAt: existingData.createdAt || new Date() as any,
            updatedAt: new Date() as any,
            // rating, review, reviewVisibility, watchedDate, lastWatchedDate are completely omitted
          };

          // Update local cache with cleaned data
          updateMovieDataInCache(movieId, cleanedData);
          console.log('UserMovieDataContext: Updated cache - kept in watchlist but removed all watch/review data');
        } else {
          // Remove completely from cache since not in watchlist
          removeMovieDataFromCache(movieId);
          console.log('UserMovieDataContext: Completely removed from cache - no longer in watchlist or watched');
        }

        // Force immediate cache version update for critical operations
        setCacheVersion(v => v + 1);
        console.log('UserMovieDataContext: Forced immediate cache version update for deleteRatingAndReview');

        // Call Firebase service to handle the deletion (also deletes crew-feed activity records)
        await movieService.deleteMovieRatingAndReview(currentUser, movieId);
        console.log('UserMovieDataContext: Firebase deletion completed');

        // Remove the story so it disappears from the Spotlights rail
        await deleteStoryForReview(currentUser.uid, movieId);

        // Update ALL localStorage locations
        const localRatings = JSON.parse(localStorage.getItem('ratings') || '{}');
        delete localRatings[movieId];
        setDebouncedItem('ratings', JSON.stringify(localRatings));

        // Remove from general reviews storage
        const localReviews = JSON.parse(localStorage.getItem('reviews') || '{}');
        delete localReviews[movieId];
        setDebouncedItem('reviews', JSON.stringify(localReviews));

        // Remove individual review storage
        localStorage.removeItem(`review_${movieId}`);

        // Remove from watched list in localStorage (always since we're unwatching)
        const localWatched = JSON.parse(localStorage.getItem('watched') || '[]');
        const updatedWatched = localWatched.filter((id: string) => id !== movieId);
        setDebouncedItem('watched', JSON.stringify(updatedWatched));

        console.log('UserMovieDataContext: Successfully completed deleteRatingAndReview operation - data completely cleaned');
        invalidateMoviePersonalityCache();
      }
    } catch (error) {
      console.error('UserMovieDataContext: Error deleting rating and review:', error);
      throw error;
    }
  }, [movieDataCache, updateMovieDataInCache, removeMovieDataFromCache, setCacheVersion, setDebouncedItem, invalidateMoviePersonalityCache]);

  const updateMovieNotes = useCallback(async (movieId: string, note: string) => {
    const currentUser = userRef.current;
    if (!currentUser) {
      throw new Error('User must be authenticated to update movie notes');
    }

    const trimmedNote = note.trim();
    const existingData = movieDataCache.get(movieId) || null;

    const baseData: UserMovieData = existingData
      ? { ...existingData }
      : {
        uid: currentUser.uid,
        movieId,
        isInWatchlist: false,
        isWatched: false,
        isFavorite: false,
        watchCount: 0,
        createdAt: new Date() as any,
        updatedAt: new Date() as any,
      };

    const updatedData: UserMovieData = {
      ...baseData,
      notes: trimmedNote || undefined,
      updatedAt: new Date() as any,
    };

    updateMovieDataInCache(movieId, updatedData);

    try {
      await movieService.saveUserMovieData(currentUser, movieId, {
        notes: trimmedNote || undefined,
      });
    } catch (error) {
      console.error('UserMovieDataContext: Error updating movie notes:', error);
      if (existingData) {
        updateMovieDataInCache(movieId, existingData);
      } else {
        removeMovieDataFromCache(movieId);
      }
      throw error;
    }
  }, [movieDataCache, updateMovieDataInCache, removeMovieDataFromCache]);

  const refreshMovieData = useCallback(async (movieId: string) => {
    const currentUser = userRef.current;
    if (!currentUser) return;

    try {
      const movieData = await movieService.getUserMovieData(currentUser, movieId);
      if (movieData) {
        updateMovieDataInCache(movieId, movieData);
      } else {
        removeMovieDataFromCache(movieId);
      }
    } catch (error) {
      console.error('Error refreshing movie data:', error);
    }
  }, [updateMovieDataInCache, removeMovieDataFromCache]);

  const refreshAllMovieData = useCallback(async () => {
    await loadInitialData();
  }, [loadInitialData]);

  const clearAllUserData = useCallback(async () => {
    const currentUser = userRef.current;
    if (!currentUser) {
      console.log('UserMovieDataContext: No user found for clearAllUserData');
      return;
    }

    try {
      invalidateMoviePersonalityCache();
      console.log('UserMovieDataContext: Starting comprehensive user data clearing...');
      console.log('UserMovieDataContext: Current cache size before deletion:', movieDataCache.size);
      console.log('UserMovieDataContext: Movies in cache:', Array.from(movieDataCache.keys()));

      // Clear comprehensive localStorage FIRST to prevent race condition
      console.log('UserMovieDataContext: Starting comprehensive localStorage cleanup...');

      // Core movie data
      localStorage.removeItem('watchlist');
      localStorage.removeItem('watched');
      localStorage.removeItem('ratings');
      localStorage.removeItem('reviews');
      localStorage.removeItem(`movieData_${currentUser.uid}`);

      // User preferences and view modes
      localStorage.removeItem('homeWatchlistViewMode');
      localStorage.removeItem('otherUserProfileViewMode');

      // Recent movies and cache
      localStorage.removeItem('recentMovies');
      localStorage.removeItem('userWatchlists');

      // TMDB and poster cache
      localStorage.removeItem('tmdb_cache');
      localStorage.removeItem('poster_cache');
      localStorage.removeItem('watchlist_posters_cache');

      // Clear any movie-specific review data
      const reviewKeys = Object.keys(localStorage).filter(key => key.startsWith('review_'));
      reviewKeys.forEach(key => localStorage.removeItem(key));
      console.log(`UserMovieDataContext: Cleared ${reviewKeys.length} review keys`);

      // Clear user-specific data (includes UID in key)
      const userKeys = Object.keys(localStorage).filter(key => key.includes(currentUser.uid));
      userKeys.forEach(key => localStorage.removeItem(key));
      console.log(`UserMovieDataContext: Cleared ${userKeys.length} user-specific keys`);

      // Clear any remaining movie or cache data
      const movieKeys = Object.keys(localStorage).filter(key =>
        key.startsWith('movie_') ||
        key.startsWith('cache_') ||
        key.startsWith('tmdb_') ||
        key.startsWith('poster_')
      );
      movieKeys.forEach(key => localStorage.removeItem(key));
      console.log(`UserMovieDataContext: Cleared ${movieKeys.length} movie/cache keys`);

      console.log('UserMovieDataContext: Cleared localStorage first');

      // Clear the entire cache immediately
      setMovieDataCache(new Map());
      setCacheVersion(v => v + 1); // Single increment for cache clear

      // Reset loading state to force fresh load from Firebase
      dataLoadedRef.current = false;
      loadingRef.current = false;

      console.log('UserMovieDataContext: Cleared cache and reset loading state');

      // Use the comprehensive Firebase service deletion method
      // This will handle batch operations properly and delete from all collections
      await dataService.deleteUserData(currentUser.uid);

      console.log('UserMovieDataContext: Firebase deletion completed');

      // Force a fresh reload from Firebase to verify data is gone
      console.log('UserMovieDataContext: Forcing fresh reload from Firebase...');
      await loadInitialData();

      console.log('UserMovieDataContext: Successfully cleared ALL user data and reloaded');
    } catch (error) {
      console.error('UserMovieDataContext: Error clearing all user data:', error);
      throw error;
    }
  }, [loadInitialData, invalidateMoviePersonalityCache]);

  const getWatchlistMovieIds = useCallback((): string[] => {
    return Array.from(movieDataCache.values())
      .filter(data => data.isInWatchlist)
      .map(data => data.movieId);
  }, [movieDataCache]);

  const getWatchedMovieIds = useCallback((): string[] => {
    const allMovies = Array.from(movieDataCache.values());
    const watchedMovies = allMovies.filter(data => data.isWatched);
    return watchedMovies.map(data => data.movieId);
  }, [movieDataCache]);

  const getRatedMovieIds = useCallback((): string[] => {
    return Array.from(movieDataCache.values())
      .filter(data => data.rating && data.rating > 0)
      .map(data => data.movieId);
  }, [movieDataCache]);

  const getReviewedMovieIds = useCallback((): string[] => {
    return Array.from(movieDataCache.values())
      .filter(data => data.isWatched === true && data.review && data.review.trim().length > 0) // ✅ Fixed: Match WatchlistFilteringService logic
      .map(data => data.movieId);
  }, [movieDataCache]);

  // Add comprehensive ID migration function
  const performComprehensiveIdMigration = useCallback(async () => {
    const currentUser = userRef.current;
    if (!currentUser || movieDataCache.size === 0) return;

    console.log('UserMovieDataContext: Starting comprehensive ID migration...');
    let migrationCount = 0;
    let cleanupCount = 0;

    try {
      // Step 1: Map all current cache entries
      const cacheEntries = Array.from(movieDataCache.entries());
      const idGroups = new Map<string, Array<{ id: string; data: UserMovieData }>>();

      // Group movies by their standardized IDs to identify duplicates
      for (const [currentId, movieData] of cacheEntries) {
        const standardizedId = getStandardizedMovieId({
          imdbID: currentId,
          id: currentId,
          Title: 'Migration'
        });

        if (!idGroups.has(standardizedId)) {
          idGroups.set(standardizedId, []);
        }
        idGroups.get(standardizedId)!.push({ id: currentId, data: movieData });
      }

      // Step 2: Process each group
      for (const [standardizedId, group] of Array.from(idGroups.entries())) {
        if (group.length === 1 && group[0].id === standardizedId) {
          // Already correct format, skip
          continue;
        }

        console.log(`UserMovieDataContext: Migrating group to ${standardizedId}:`, group.map((g: { id: string; data: UserMovieData }) => g.id));

        // Merge all data for this movie
        const mergedData: UserMovieData = {
          uid: currentUser.uid,
          movieId: standardizedId,
          isInWatchlist: false,
          isWatched: false,
          isFavorite: false,
          watchCount: 0,
          createdAt: new Date() as any,
          updatedAt: new Date() as any,
        };

        // Merge data from all entries
        for (const entry of group) {
          if (entry.data.isInWatchlist) mergedData.isInWatchlist = true;
          if (entry.data.isWatched) mergedData.isWatched = true;
          if (entry.data.isFavorite) mergedData.isFavorite = true;
          if (entry.data.rating && !mergedData.rating) mergedData.rating = entry.data.rating;
          if (entry.data.review && !mergedData.review) mergedData.review = entry.data.review;
          if (entry.data.watchedDate && !mergedData.watchedDate) mergedData.watchedDate = entry.data.watchedDate;
          if (entry.data.watchCount) mergedData.watchCount = Math.max(mergedData.watchCount, entry.data.watchCount);
          // Keep earliest creation date
          if (entry.data.createdAt && (mergedData.createdAt as any) > (entry.data.createdAt as any)) {
            mergedData.createdAt = entry.data.createdAt;
          }
        }

        // Step 3: Save merged data to Firebase with standardized ID
        await movieService.saveUserMovieData(currentUser, standardizedId, mergedData);

        // Step 4: Clean up old entries
        for (const entry of group) {
          if (entry.id !== standardizedId) {
            try {
              await movieService.deleteUserMovieData(currentUser, entry.id);
              removeMovieDataFromCache(entry.id);
              cleanupCount++;
            } catch (error) {
              console.warn(`UserMovieDataContext: Error cleaning up ${entry.id}:`, error);
            }
          }
        }

        // Step 5: Update cache with merged data
        updateMovieDataInCache(standardizedId, mergedData);
        migrationCount++;
      }

      // Step 6: Clean up localStorage with old formats
      const watchedList = JSON.parse(localStorage.getItem('watched') || '[]');
      const migratedWatchedList = watchedList.map((id: string) => getStandardizedMovieId({ imdbID: id, id }));
      localStorage.setItem('watched', JSON.stringify(Array.from(new Set(migratedWatchedList))));

      const ratingsObj = JSON.parse(localStorage.getItem('ratings') || '{}');
      const migratedRatings: any = {};
      for (const [id, rating] of Object.entries(ratingsObj)) {
        const standardizedId = getStandardizedMovieId({ imdbID: id, id });
        migratedRatings[standardizedId] = rating;
      }
      localStorage.setItem('ratings', JSON.stringify(migratedRatings));

      console.log(`UserMovieDataContext: Migration completed. Migrated ${migrationCount} groups, cleaned up ${cleanupCount} old entries`);

      // Force cache version update to trigger UI refresh (migration complete)
      setCacheVersion(v => v + 1);

    } catch (error) {
      console.error('UserMovieDataContext: Error during ID migration:', error);
    }
  }, [movieDataCache, updateMovieDataInCache, removeMovieDataFromCache]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo<UserMovieDataContextType>(() => ({
    movieDataCache,
    isLoading,
    cacheVersion,
    getUserMovieData,
    updateMovieData,
    addToWatchlist,
    removeFromWatchlist,
    markAsWatched,
    unmarkAsWatched,
    deleteRatingAndReview,
    updateMovieNotes,
    refreshMovieData,
    refreshAllMovieData,
    getWatchlistMovieIds,
    getWatchedMovieIds,
    getRatedMovieIds,
    getReviewedMovieIds,
    clearAllUserData,
  }), [
    movieDataCache,
    isLoading,
    cacheVersion,
    getUserMovieData,
    updateMovieData,
    addToWatchlist,
    removeFromWatchlist,
    markAsWatched,
    unmarkAsWatched,
    deleteRatingAndReview,
    updateMovieNotes,
    refreshMovieData,
    refreshAllMovieData,
    getWatchlistMovieIds,
    getWatchedMovieIds,
    getRatedMovieIds,
    getReviewedMovieIds,
    clearAllUserData,
  ]);

  return (
    <UserMovieDataContext.Provider value={contextValue}>
      {children}
    </UserMovieDataContext.Provider>
  );
}; 
